import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Chip, Icon, Skeleton, type ChipTone } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { useConversationLookups } from "../shared/customer-context/customer-context-api";
import type { CustomerLookup } from "../shared/customer-context/customer-context-model";

const OUTCOME_CHIP: Record<string, { tone: ChipTone; icon: "check-circle" | "clock" | "x-circle" }> = {
  ok: { tone: "success", icon: "check-circle" },
  stale: { tone: "warning", icon: "clock" },
  failed: { tone: "danger", icon: "x-circle" },
};
const UNKNOWN_OUTCOME = { tone: "muted", icon: "clock" } as const;

function LookupRow({ item }: { item: CustomerLookup }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const outcome = OUTCOME_CHIP[item.outcome] ?? UNKNOWN_OUTCOME;
  const operation = t(`customerContext.lookups.operations.${item.operation}`, { defaultValue: item.operation.replace(/_/g, " ") });
  return (
    <li className="space-y-1" data-testid="lookup-row">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 font-semibold">{operation}</span>
        <time dateTime={item.at} className="shrink-0 text-fg-muted">
          {fmt.time(item.at)}
        </time>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={outcome.tone} icon={outcome.icon}>
          {t(`customerContext.lookups.outcome.${item.outcome}`, { defaultValue: item.outcome })}
        </Chip>
        {item.bound_to_session_customer ? (
          <Chip tone="brand" icon="shield-check">
            {t("customerContext.lookups.bound")}
          </Chip>
        ) : null}
        {item.connector ? <span className="text-fg-muted">{t("customerContext.lookups.via", { connector: item.connector })}</span> : null}
      </div>
    </li>
  );
}

/** Inbox pin 4: the lookups made in this chat, each shown as bound to the session's customer. */
export function LookupLog({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation();
  const query = useConversationLookups(conversationId);
  if (query.isError && isApiError(query.error) && (query.error.status === 403 || query.error.status === 404)) return null;
  return (
    <div className="space-y-2 border-t border-line pt-3" data-testid="lookup-log">
      <h4 className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">{t("customerContext.lookups.title")}</h4>
      {query.isPending ? (
        <div role="status" className="space-y-2">
          <span className="sr-only">{t("customerContext.card.loading")}</span>
          <Skeleton className="h-8 w-full" />
        </div>
      ) : null}
      {query.isError ? (
        <p role="alert" className="text-xs text-danger">
          {t("customerContext.lookups.errorTitle")}{" "}
          <button type="button" className="font-semibold underline focus-visible:outline-2 focus-visible:outline-primary" onClick={() => void query.refetch()}>
            {t("customerContext.lookups.retry")}
          </button>
        </p>
      ) : null}
      {query.data?.items.length === 0 ? (
        <div className="flex items-start gap-2 text-xs text-fg-muted">
          <Icon name="search" className="mt-0.5 size-3.5" />
          <p>
            <span className="block font-semibold text-fg">{t("customerContext.lookups.empty")}</span>
            {t("customerContext.lookups.emptyHint")}
          </p>
        </div>
      ) : null}
      {query.data && query.data.items.length > 0 ? (
        <ul className="space-y-3 text-xs" aria-label={t("customerContext.lookups.title")}>
          {query.data.items.map((item) => (
            <LookupRow key={`${item.at}-${item.operation}-${item.outcome}`} item={item} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
